import React from "react";
import { useNavigate } from "react-router-dom";
import { Zap, ArrowLeft } from "lucide-react";

const LegalShell = ({ title, updated, children }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen text-slate-200" style={{ background: "#07080D" }} data-testid="legal-page">
      <header className="border-b" style={{ borderColor: "rgba(99,102,241,0.18)" }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5" data-testid="legal-home-link">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "linear-gradient(135deg,#6366F1,#8B5CF6)" }}><Zap className="w-4 h-4 text-white" /></div>
            <span className="font-display font-extrabold text-lg">Momento</span>
          </button>
          <button onClick={() => navigate("/")} className="ml-auto inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition-colors"><ArrowLeft className="w-4 h-4" />Back to home</button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl">{title}</h1>
        <p className="text-slate-500 text-sm mt-2">Last updated: {updated}</p>
        <div className="prose-legal mt-10 space-y-8">{children}</div>
        <p className="text-slate-500 text-sm mt-14 border-t pt-6" style={{ borderColor: "rgba(148,163,184,0.14)" }}>
          Questions? Contact us at <a href="mailto:info@momentocrm.com" className="text-indigo-400 hover:text-indigo-300">info@momentocrm.com</a>.
        </p>
      </main>
    </div>
  );
};

const Section = ({ n, title, children }) => (
  <section>
    <h2 className="font-display font-bold text-xl text-white">{n}. {title}</h2>
    <div className="text-slate-300 text-[15px] leading-relaxed mt-3 space-y-3">{children}</div>
  </section>
);

export default function TermsOfService() {
  return (
    <LegalShell title="Terms of Service" updated="June 2026">
      <p className="text-slate-300 text-[15px] leading-relaxed">
        These Terms of Service ("Terms") govern your access to and use of Momento (the "Service"), operated by
        Momento ("we", "us", "our"). By creating an account or using the Service, you agree to these Terms. If you
        do not agree, do not use the Service.
      </p>

      <Section n="1" title="Accounts & Eligibility">
        <p>You must provide accurate information when registering and are responsible for safeguarding your login
          credentials and all activity under your account. You must be at least 18 years old and able to form a
          binding contract. You are responsible for your sub-accounts and any users you invite.</p>
      </Section>

      <Section n="2" title="Subscriptions, Trials & Billing">
        <p>Momento is offered on subscription plans billed monthly or annually. Paid plans may begin with a free
          trial; a valid payment method is required to start a trial, and unless you cancel before the trial ends,
          your subscription will automatically convert to a paid plan and your payment method will be charged.</p>
        <p>Fees are billed in advance and are non-refundable except where required by law. You can cancel at any
          time from the in-app Billing page; cancellation takes effect at the end of the current billing period.
          Payments are processed by Stripe, and your use of payment features is also subject to Stripe's terms.</p>
        <p>We may change plan pricing with reasonable prior notice; changes apply to the next billing cycle.</p>
      </Section>

      <Section n="3" title="Acceptable Use">
        <p>You agree not to misuse the Service, including sending unsolicited or unlawful communications (spam),
          violating anti-spam, telemarketing, or data-protection laws, infringing others' rights, attempting to
          gain unauthorized access, or interfering with the Service's operation. You are solely responsible for
          the content you send and the consent you obtain from your contacts.</p>
      </Section>

      <Section n="4" title="Your Content & Data">
        <p>You retain ownership of the data and content you upload ("Customer Data"). You grant us a limited license
          to host and process Customer Data solely to provide the Service. You represent that you have the rights
          and lawful basis to collect and process the contact data you store in Momento.</p>
      </Section>

      <Section n="5" title="Third-Party Services">
        <p>The Service integrates with third parties (e.g., Stripe for payments, email and messaging providers).
          Your use of those integrations may be subject to their respective terms, and we are not responsible for
          third-party services.</p>
      </Section>

      <Section n="6" title="Intellectual Property">
        <p>The Service, including its software, design, and trademarks, is owned by Momento and protected by law.
          We grant you a non-exclusive, non-transferable right to use the Service during your subscription. You may
          not copy, reverse engineer, or resell the Service except as expressly permitted.</p>
      </Section>

      <Section n="7" title="Termination">
        <p>You may stop using the Service at any time. We may suspend or terminate access if you breach these Terms
          or use the Service in a way that risks harm to us or others. Upon termination, your right to use the
          Service ends; we may delete Customer Data after a reasonable retention period.</p>
      </Section>

      <Section n="8" title="Disclaimers & Limitation of Liability">
        <p>The Service is provided "as is" without warranties of any kind to the maximum extent permitted by law.
          We are not liable for indirect, incidental, or consequential damages, and our total liability is limited
          to the amounts you paid to us in the twelve months preceding the claim.</p>
      </Section>

      <Section n="9" title="Changes to These Terms">
        <p>We may update these Terms from time to time. If we make material changes, we will notify you. Your
          continued use of the Service after changes take effect constitutes acceptance.</p>
      </Section>

      <Section n="10" title="Contact">
        <p>For any questions about these Terms, contact us at info@momentocrm.com.</p>
      </Section>
    </LegalShell>
  );
}
